/* Query thread attachments: thumbnails / PDF chips loaded with auth, plus an attach button. */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, FileText, Loader2, Camera, Send, X } from "lucide-react";
import { API, getAuthHeader } from "@/App";

const Slot = ({ label, file, setFile }) => {
  const ref = useRef(null);
  const url = file ? URL.createObjectURL(file) : "";
  return (
    <div className="flex items-center gap-1">
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`pq-photo-${label}-input`}
        onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
      {file ? (
        <span className="relative">
          <img src={url} alt={label} className="h-12 w-12 rounded border object-cover" />
          <button type="button" onClick={() => setFile(null)} className="absolute -right-1 -top-1 rounded-full bg-white shadow" title="Remove"><X className="h-3 w-3" /></button>
        </span>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} data-testid={`pq-photo-${label}-btn`}
          className="inline-flex h-12 items-center gap-1 rounded border border-dashed border-teal-400 bg-white px-2 text-[11px] text-teal-700 hover:bg-teal-50">
          <Camera className="h-4 w-4" /> {label === "inside" ? "Inside" : "Outside"}</button>
      )}
    </div>
  );
};

export const PackingPhotos = ({ rowId, onDone }) => {
  const [inside, setInside] = useState(null);
  const [outside, setOutside] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (inside) fd.append("inside", inside);
      if (outside) fd.append("outside", outside);
      await axios.post(`${API}/order-register/queries/by-row/${rowId}/packing-photos`, fd, { headers: getAuthHeader() });
      toast.success("Packing photos sent to the query thread");
      setInside(null); setOutside(null); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-teal-200 bg-teal-50/50 p-2" data-testid="pq-packing-photos">
      <span className="text-[11px] font-semibold text-teal-800">Send packing photos:</span>
      <Slot label="inside" file={inside} setFile={setInside} />
      <Slot label="outside" file={outside} setFile={setOutside} />
      <button type="button" disabled={busy || (!inside && !outside)} onClick={send} data-testid="pq-photo-send"
        className="ml-auto inline-flex items-center gap-1 rounded bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send</button>
    </div>
  );
};

const blobCache = {};
const fetchBlob = async (id) => {
  if (!blobCache[id]) {
    const r = await axios.get(`${API}/order-register/queries/files/${id}`, { headers: getAuthHeader(), responseType: "blob" });
    blobCache[id] = URL.createObjectURL(r.data);
  }
  return blobCache[id];
};

const FileChip = ({ f }) => {
  const [url, setUrl] = useState(blobCache[f.id] || "");
  const img = f.content_type?.startsWith("image/");
  useEffect(() => { if (img && !url) fetchBlob(f.id).then(setUrl).catch(() => {}); }, [f.id, img, url]);
  const open = async () => { try { window.open(await fetchBlob(f.id), "_blank"); } catch { toast.error("Could not open file"); } };
  return img
    ? <button type="button" onClick={open} title={f.filename} data-testid={`pq-file-${f.id}`}>
        {url ? <img src={url} alt={f.filename} className="h-16 w-16 rounded border object-cover" /> : <div className="grid h-16 w-16 place-items-center rounded border"><Loader2 className="h-3 w-3 animate-spin" /></div>}</button>
    : <button type="button" onClick={open} className="inline-flex items-center gap-1 rounded border bg-white px-2 py-1 text-[11px] text-sky-700" data-testid={`pq-file-${f.id}`}>
        <FileText className="h-3.5 w-3.5" /> {f.filename}</button>;
};

export const FileChips = ({ files }) => (!files?.length ? null : (
  <div className="mt-1 flex flex-wrap gap-1.5">{files.map((f) => <FileChip key={f.id} f={f} />)}</div>));

export const AttachButton = ({ qid, text, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (text?.trim()) fd.append("text", text.trim());
    setBusy(true);
    try { const r = await axios.post(`${API}/order-register/queries/${qid}/files`, fd, { headers: getAuthHeader() }); toast.success("Attached"); onDone(r.data); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  return (<>
    <input ref={ref} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid="pq-attach-input" />
    <button type="button" onClick={() => ref.current?.click()} disabled={busy} title="Attach photo or PDF (max 10 MB, 5 files)"
      className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs text-slate-700 hover:bg-slate-50" data-testid="pq-attach-btn">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} Attach</button>
  </>);
};
